"use client";

import React, { useState } from "react";
import { Search, Plus, Edit3, Trash2, Tag, Filter, ChevronDown, ChevronUp, Layers, Table, Edit3Icon, LucideTrash2, LucideEdit3 } from "lucide-react";
import ProductTypeModal from "./ProductTypeModal";
import { useProductTypes } from "../context/ProductTypeContext";

/* ─── Toggle Switch Helper ─── */
const ToggleSwitch = ({ isActive, onToggle, id, status }) => (
  <button
    onClick={() => onToggle(id, status)}
    title={isActive ? "Set Inactive" : "Set Active"}
    className={`relative inline-flex items-center w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
      isActive
        ? "bg-emerald-500 focus:ring-emerald-400"
        : "bg-gray-300 focus:ring-gray-400"
    }`}
  >
    <span
      className={`inline-block w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform duration-200 ${
        isActive ? "translate-x-4" : "translate-x-0.5"
      }`}
    />
  </button>
);

export default function ProductTypesPage() {
  const {
    productTypes,
    producttypeloading,
    addProductType,
    updateProductType,
    deleteProductType,
    toggleStatus,
  } = useProductTypes();

  const [search, setSearch] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [viewType, setViewType] = useState("list");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const handleDeleteClick = (type) => {
    setSelectedType(type);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteProductType(selectedType.id);
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("A-Z");

  const openAdd = () => {
    setEditingType(null);
    setModalOpen(true);
  };

  const openEdit = (type) => {
    setEditingType(type);
    setModalOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (editingType) {
        await updateProductType(editingType.id, data);
      } else {
        await addProductType(data);
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  let filteredTypes = productTypes.filter((type) =>
    (type?.type_name || "").toLowerCase().includes(search.toLowerCase())
  );

  if (statusFilter !== "All") {
    filteredTypes = filteredTypes.filter((type) => type.status === statusFilter);
  }

  if (sortBy === "A-Z") {
    filteredTypes.sort((a, b) => (a?.type_name || "").localeCompare(b?.type_name || ""));
  }

  if (sortBy === "Z-A") {
    filteredTypes.sort((a, b) => (b?.type_name || "").localeCompare(a?.type_name || ""));
  }

  const activeCount = productTypes.filter(t => t.status === "Active").length;
  const inactiveCount = productTypes.filter(t => t.status === "Inactive").length;
  const totalCount = productTypes.length;

  return (
    <div className="p-6 bg-[#fff8ef] min-h-screen">

      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="bg-teal-700 text-white p-2 rounded-lg">
            <Tag size={28} />
          </div>
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-teal-800">
              Product Types
            </h1>
            <p className="text-gray-600 text-xs md:text-sm">
              Manage product types for your products
            </p>
          </div>
        </div>
        <button
          onClick={openAdd}
           className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
          >
          <Plus size={16} />
          <span className="hidden sm:inline">Add Product Type</span>
        </button>
      </div>

      {/* ── Search + Filter Row — matches Products page style ── */}
      <section className="bg-white bg-opacity-60 rounded-xl border border-orange-200 p-6 mb-8 hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300">
        <div className="flex flex-col md:flex-row md:items-center gap-4">

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search product types..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Filter Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-teal-600 hover:text-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-lg p-2 border border-teal-200 bg-teal-50 w-fit"
          >
            <Filter className="w-5 h-5" />
            <span className="font-medium">Filters</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
              >
                <option value="All">All Types ({totalCount})</option>
                <option value="Active">Active ({activeCount})</option>
                <option value="Inactive">Inactive ({inactiveCount})</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
              >
                <option value="A-Z">Name A-Z</option>
                <option value="Z-A">Name Z-A</option>
              </select>
            </div>

          </div>
        )}
      </section>

      {/* Showing count + View Toggle */}
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-gray-500">
          Showing{" "}
          <span className="font-semibold text-teal-700">{filteredTypes.length}</span>{" "}
          of{" "}
          <span className="font-semibold text-gray-700">{productTypes.length}</span>{" "}
          Product Types
        </span>

        <div className="flex items-center gap-1 p-1 rounded-lg">
          <button
            onClick={() => setViewType("list")}
            title="List"
            className={`p-2 rounded-md transition-all ${
              viewType === "list"
                ? "bg-teal-600 text-white shadow-sm"
                : "text-gray-500 hover:text-teal-600 hover:bg-white"
            }`}
          >
            <Layers size={17} />
          </button>
          <button
            onClick={() => setViewType("table")}
            title="Table"
            className={`p-2 rounded-md transition-all ${
              viewType === "table"
                ? "bg-teal-600 text-white shadow-sm"
                : "text-gray-500 hover:text-teal-600 hover:bg-white"
            }`}
          >
            <Table size={17} />
          </button>
        </div>
      </div>

      {/* List View */}
      {viewType === "list" && (
        <div className="flex flex-col gap-4">
          {filteredTypes.map((type) => (
            <div
              key={type.id}
              className="bg-white border rounded-xl p-4 flex items-center justify-between hover:shadow-md transition"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                  <Tag className="text-orange-500" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold">
                    {type?.type_name || "Unnamed Type"}
                  </h3>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      type.status === "Active"
                        ? "bg-green-100 text-green-600"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {type.status}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <ToggleSwitch
                  isActive={type.status === "Active"}
                  onToggle={toggleStatus}
                  id={type.id}
                  status={type.status}
                />
                <button
                  onClick={() => openEdit(type)}
                  className="p-2 rounded-lg text-teal-600 hover:text-teal-600 hover:bg-teal-50 transition-all"
                  title="Edit"
                >
                  <Edit3Icon size={16} />
                </button>
                <button
                  onClick={() => handleDeleteClick(type)}
                  className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all"
                  title="Delete"
                >
                  <LucideTrash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewType === "table" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-teal-50 to-orange-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTypes.map((type, idx) => (
                <tr
                  key={type.id}
                  className={`hover:bg-teal-50/40 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                >
                  <td className="px-4 py-3 font-medium text-gray-800">{type.type_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <ToggleSwitch
                        isActive={type.status === "Active"}
                        onToggle={toggleStatus}
                        id={type.id}
                        status={type.status}
                      />
                      <button
                        onClick={() => openEdit(type)}
                        className="p-2 rounded-lg text-teal-700 hover:text-teal-600 hover:bg-teal-50 transition-all"
                        title="Edit"
                      >
                        <LucideEdit3 size={15} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(type)}
                        className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all"
                        title="Delete"
                      >
                        <LucideTrash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Table Footer */}
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td colSpan={2} className="px-4 py-3">
                  <p className="text-xs text-gray-500">
                    {filteredTypes.length} type{filteredTypes.length !== 1 ? "s" : ""}
                  </p>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Empty State */}
      {filteredTypes.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-20">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Tag className="text-gray-400" size={36} />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No product types found
          </h3>
          <p className="text-gray-500 text-sm mb-6">
            Try adjusting your search or add a new product type.
          </p>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-teal-700 text-white px-5 py-2.5 rounded-lg hover:bg-orange-600"
          >
            <Plus size={18} />
            Add Product Type
          </button>
        </div>
      )}

      {/* Add / Edit Modal */}
      <ProductTypeModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        productType={editingType}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-lg w-[90%] max-w-md p-6 z-10">
            <h2 className="text-lg font-semibold mb-2">Delete Product Type</h2>
            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-800">
                {selectedType?.type_name}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}